import React from 'react';

import {
  TARIFF_TIME_UNIT_DAY, TARIFF_TIME_UNIT_MONTH, TARIFF_TIME_UNIT_YEAR,
} from 'src/util/constants';

/**
 * Builds a complete rate expression with proper language structure
 * This holistic approach handles the entire rate formatting including currency and time unit
 * positioning according to language-specific grammatical rules.
 * @param {import('react-intl').IntlShape} intl - The intl object
 * @param {number} value - The numeric rate value
 * @param {string} currencySymbol - Currency symbol (e.g., '$', '¥', '€')
 * @param {TARIFF_TIME_UNIT_DAY | TARIFF_TIME_UNIT_MONTH | TARIFF_TIME_UNIT_YEAR} timeUnit
 * @returns {React.ReactNode | string | null} Complete formatted rate expression
 * @example
 * // English (en-AU): Daily rate with AUD
 * buildValuePerTimeUnit(intl, 10.50, '$', TARIFF_TIME_UNIT_DAY)
 * // Returns: "$10.50<abbr title="per day">/day</abbr>"
 * @example
 * // English (en-GB): Monthly rate with GBP
 * buildValuePerTimeUnit(intl, 25.75, '£', TARIFF_TIME_UNIT_MONTH)
 * // Returns: "£25.75<abbr title="per month">/month</abbr>"
 * @example
 * // Japanese (ja-JP): Yearly rate with JPY (different structure)
 * buildValuePerTimeUnit(intl, 1200, '¥', TARIFF_TIME_UNIT_YEAR)
 * // Returns: "1年1,200¥" (proper Japanese structure)
 * @example
 * // Italian (it-IT): Daily rate with EUR (different structure)
 * buildValuePerTimeUnit(intl, 5.25, '€', TARIFF_TIME_UNIT_DAY)
 * // Returns: "5,25 € al giorno" (proper Italian structure)
 */
const buildValuePerTimeUnit = (intl, value, currencySymbol, timeUnit) => {
  if (!intl || value === undefined || !currencySymbol || !timeUnit) {
    return null;
  }

  switch (timeUnit) {
    case TARIFF_TIME_UNIT_DAY: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_day.abbr.title',
        defaultMessage: 'per day',
        description: 'Accessibility title for daily rate abbreviation',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_day.text_with_abbr',
        defaultMessage: '{currencySymbol}{value, number, :: .00#######}<abbr>/day</abbr>',
      }, {
        currencySymbol,
        value,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    case TARIFF_TIME_UNIT_MONTH: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_month.abbr.title',
        defaultMessage: 'per month',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_month.abbr.text',
        defaultMessage: '{currencySymbol}{value, number, :: .00#######}<abbr>/month</abbr>',
      }, {
        currencySymbol,
        value,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    case TARIFF_TIME_UNIT_YEAR: {
      const abbrTitle = intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_year.abbr.title',
        defaultMessage: 'per year',
      });

      return intl.formatMessage({
        id: 'util.i18n.value_per_time_unit.per_year.abbr.text',
        defaultMessage: '{currencySymbol}{value, number, :: .00#######}<abbr>/year</abbr>',
      }, {
        currencySymbol,
        value,
        abbr: (str) => <abbr title={abbrTitle}>{str}</abbr>,
      });
    }
    default:
      return null;
  }
};

export default buildValuePerTimeUnit;
